import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AnalysisResults, SkinMetrics, Heatmaps } from '@/types/analysis'
import {
  saveAnalysis,
  getAnalysisHistory,
  deleteTeaserAnalysis,
  type AnalysisSummary,
  type SaveAnalysisRequest,
} from '@/services/api'
import { useSurveyStore } from './surveyStore'
import { useFullSurveyStore } from './fullSurveyStore'

interface AnalysisState {
  // Analysis results
  analysisResults: AnalysisResults | null
  isAnalyzing: boolean
  error: string | null

  // Photo storage (S3)
  photoId: string | null  // S3 photo ID for HD re-analysis

  // History (from backend)
  analysisHistory: AnalysisSummary[]
  historyLoaded: boolean

  // Actions
  setAnalysisResults: (results: AnalysisResults) => void
  setAnalyzing: (isAnalyzing: boolean) => void
  setError: (error: string | null) => void
  updateMetrics: (metrics: Partial<SkinMetrics>) => void
  updateHeatmaps: (heatmaps: Partial<Heatmaps>) => void
  setPhotoUrl: (url: string) => void
  setPhotoId: (photoId: string) => void

  // Backend sync
  saveToBackend: (mode: 'teaser' | 'full') => Promise<string | null>
  loadHistory: () => Promise<void>

  // Computed getters
  hasResults: () => boolean
  hasHDResults: () => boolean  // Check if HD analysis (14 metrics) has been done

  // Reset
  resetAnalysis: () => void
  clearForRetake: () => void  // Clear HD data for photo retake (keeps basic teaser data)
}

export const useAnalysisStore = create<AnalysisState>()(
  persist(
    (set, get) => ({
      analysisResults: null,
      isAnalyzing: false,
      error: null,
      photoId: null,
      analysisHistory: [],
      historyLoaded: false,

      setAnalysisResults: (results) =>
        set({
          analysisResults: results,
          isAnalyzing: false,
          error: null,
        }),

      setAnalyzing: (isAnalyzing) =>
        set({ isAnalyzing }),

      setError: (error) =>
        set({ error, isAnalyzing: false }),

      updateMetrics: (metrics) =>
        set((state) => ({
          analysisResults: state.analysisResults
            ? {
                ...state.analysisResults,
                metrics: { ...state.analysisResults.metrics, ...metrics },
              }
            : null,
        })),

      updateHeatmaps: (heatmaps) =>
        set((state) => ({
          analysisResults: state.analysisResults
            ? {
                ...state.analysisResults,
                heatmaps: { ...state.analysisResults.heatmaps, ...heatmaps },
              }
            : null,
        })),

      setPhotoUrl: (url) =>
        set((state) => ({
          analysisResults: state.analysisResults
            ? { ...state.analysisResults, photoUrl: url }
            : null,
        })),

      setPhotoId: (photoId) =>
        set({ photoId }),

      hasResults: () => get().analysisResults !== null,

      // Check if HD analysis has been done by checking for HD-only heatmaps
      hasHDResults: () => {
        const results = get().analysisResults
        if (!results) return false
        // HD results have additional heatmaps like acne, oiliness, etc.
        return !!(results.heatmaps?.acne || results.heatmaps?.oiliness || results.heatmaps?.darkCircles)
      },

      saveToBackend: async (mode) => {
        const { analysisResults, photoId } = get()
        if (!analysisResults || !photoId) {
          console.error('[AnalysisStore] Cannot save: missing results or photoId')
          return null
        }

        try {
          // Get survey data based on mode
          const shortSurvey = useSurveyStore.getState().shortSurvey
          const fullSurvey = useFullSurveyStore.getState().fullSurvey

          // Merge surveys for full mode
          const surveyData = mode === 'full'
            ? { ...shortSurvey, ...fullSurvey }
            : shortSurvey

          const request: SaveAnalysisRequest = {
            photoId,
            mode,
            skinAge: analysisResults.skinAge,
            skinScore: analysisResults.skinScore,
            metrics: analysisResults.metrics as unknown as Record<string, number>,
            heatmaps: analysisResults.heatmaps as unknown as Record<string, string>,
            surveyData: surveyData as unknown as Record<string, unknown>,
          }

          console.log('[AnalysisStore] Saving analysis to backend...')
          const result = await saveAnalysis(request)
          console.log('[AnalysisStore] Saved analysis:', result.analysisId)

          // If full analysis, delete the teaser record (it's now redundant)
          // Full analysis includes all teaser metrics plus more
          if (mode === 'full') {
            try {
              console.log('[AnalysisStore] Deleting teaser analysis (now redundant)...')
              await deleteTeaserAnalysis(photoId)
              console.log('[AnalysisStore] Teaser analysis deleted')
            } catch (deleteError) {
              // Non-fatal: teaser may not exist or already deleted
              console.warn('[AnalysisStore] Could not delete teaser:', deleteError)
            }
          }

          return result.analysisId
        } catch (error) {
          console.error('[AnalysisStore] Failed to save analysis:', error)
          return null
        }
      },

      loadHistory: async () => {
        if (get().historyLoaded) return

        try {
          console.log('[AnalysisStore] Loading analysis history...')
          const { analyses } = await getAnalysisHistory()
          set({
            analysisHistory: analyses,
            historyLoaded: true,
          })
          console.log('[AnalysisStore] Loaded', analyses.length, 'analyses')
        } catch (error) {
          console.error('[AnalysisStore] Failed to load history:', error)
          set({ historyLoaded: true }) // Mark as loaded to prevent retries
        }
      },

      resetAnalysis: () =>
        set({
          analysisResults: null,
          isAnalyzing: false,
          error: null,
          photoId: null,
          analysisHistory: [],
          historyLoaded: false,
        }),

      // Clear for photo retake - clears photoId to allow new photo upload
      // Analysis results are kept as reference until new results come in
      clearForRetake: () =>
        set({
          photoId: null, // Clear photoId to allow new photo upload
        }),
    }),
    {
      name: 'k-skin-analysis',
      partialize: (state) => ({
        analysisResults: state.analysisResults,
        photoId: state.photoId,
      }),
    }
  )
)
